import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Card } from "../../../components/Card";
import { SectionTitle } from "../../../components/SectionTitle";
import { Button } from "../../../components/Button";
import { FormActions } from "../../../components/FormActions";
import { InfoBox } from "../../../components/InfoBox";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../../../components/Table";
import type { ImportGiftcardsDryRunResults, ImportGiftcardError } from "../../../api/giftcardsApi";
import { ValidGiftcardsTable } from "./ValidGiftcardsTable";

const ResultsCard = styled(Card)`
  margin-top: ${spacing.x4}px;
`;

const SectionDescription = styled.p`
  margin: 0 0 ${spacing.x3}px 0;
  max-width: 52rem;
  font-size: 14px;
  line-height: 1.55;
  color: ${colors.textMuted};
`;

const MetadataContainer = styled.div`
  margin-bottom: ${spacing.x3}px;
  padding: ${spacing.x2}px;
  background-color: ${colors.backgroundLight};
  border-radius: ${borderRadius.regular}px;
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${spacing.x2}px;
`;

const MetadataItem = styled.div``;

const MetadataLabel = styled.div`
  font-size: 13px;
  color: ${colors.textMuted};
`;

const MetadataValue = styled.div<{ $color?: string }>`
  font-weight: 600;
  color: ${({ $color }) => $color || colors.text};
`;

const ErrorsContainer = styled.div`
  margin-top: ${spacing.x3}px;
`;

const ErrorsTitle = styled.div`
  margin-bottom: ${spacing.x2}px;
  font-weight: 600;
`;

const ErrorValueCell = styled.div`
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledFormActions = styled(FormActions)`
  margin-top: ${spacing.x4}px;
`;

const normalizeErrors = (errors: ImportGiftcardError[]) => {
  return errors.map((e) => ({
    row: e.row,
    field: e.field ?? "",
    message: e.message,
    value:
      e.value === undefined || e.value === null ? "" : String(e.value),
  }));
};

interface GiftcardDryRunResultsProps {
  results: ImportGiftcardsDryRunResults;
  onImport: () => void;
  canImport: boolean;
  isImporting: boolean;
}

export const GiftcardDryRunResults = ({
  results,
  onImport,
  canImport,
  isImporting,
}: GiftcardDryRunResultsProps) => {
  const { t } = useTranslation();
  const errors = results.errors || [];
  const validGiftCards = results.validGiftCards || [];
  const hasErrors = errors.length > 0;
  const displayErrors = normalizeErrors(errors);

  return (
    <ResultsCard>
      <SectionTitle as="h3">{t("giftcardImport.dryRunResultsTitle")}</SectionTitle>
      <SectionDescription>{t("giftcardImport.dryRunResultsDescription")}</SectionDescription>
      <MetadataContainer>
        <MetadataGrid>
          <MetadataItem>
            <MetadataLabel>{t("giftcardImport.metaTotalRows")}</MetadataLabel>
            <MetadataValue>{results.totalRows}</MetadataValue>
          </MetadataItem>
          <MetadataItem>
            <MetadataLabel>{t("giftcardImport.metaValidRows")}</MetadataLabel>
            <MetadataValue $color={colors.success}>{results.validRows}</MetadataValue>
          </MetadataItem>
          <MetadataItem>
            <MetadataLabel>{t("giftcardImport.metaErrors")}</MetadataLabel>
            <MetadataValue $color={errors.length > 0 ? colors.danger : colors.text}>
              {results.errorCount}
            </MetadataValue>
          </MetadataItem>
          <MetadataItem>
            <MetadataLabel>{t("giftcardImport.metaStatus")}</MetadataLabel>
            <MetadataValue $color={results.success ? colors.success : colors.danger}>
              {results.success ? t("giftcardImport.metaSuccess") : t("giftcardImport.metaFailed")}
            </MetadataValue>
          </MetadataItem>
        </MetadataGrid>
      </MetadataContainer>

      {hasErrors ? (
        <>
          <InfoBox variant="error">
            {errors.length === 1
              ? t("giftcardImport.errorsFoundOne")
              : t("giftcardImport.errorsFoundMany", { count: errors.length })}
          </InfoBox>
          <ErrorsContainer>
            <ErrorsTitle>{t("giftcardImport.errorsTitle", { count: errors.length })}</ErrorsTitle>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell align="right">{t("giftcardImport.tableRow")}</TableHeaderCell>
                  <TableHeaderCell>{t("giftcardImport.tableField")}</TableHeaderCell>
                  <TableHeaderCell>{t("giftcardImport.tableMessage")}</TableHeaderCell>
                  <TableHeaderCell>{t("giftcardImport.tableValue")}</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {displayErrors.map((error, index) => (
                  <TableRow key={index}>
                    <TableCell align="right">{error.row}</TableCell>
                    <TableCell>{error.field}</TableCell>
                    <TableCell>{error.message}</TableCell>
                    <TableCell>
                      <ErrorValueCell title={error.value}>{error.value}</ErrorValueCell>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ErrorsContainer>
        </>
      ) : (
        <>
          <InfoBox variant="success">{t("giftcardImport.dryRunSuccessNoErrors")}</InfoBox>
          <ValidGiftcardsTable giftcards={validGiftCards} />
        </>
      )}

      <StyledFormActions>
        <Button onClick={onImport} disabled={!canImport || isImporting}>
          {isImporting ? t("giftcardImport.importing") : t("giftcardImport.importGiftcards")}
        </Button>
      </StyledFormActions>
    </ResultsCard>
  );
};
